import { avatarSrc, onAvatarError } from "../../utils/avatar";
import React, { useState, useEffect, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getUserFromLocalStorage } from "../../utils/localStorage";
import { ROLE } from "../../constants/constants";
import { SocketContext } from "../../context/socket";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { deleteCurrentTask } from "../../redux/currentSlice";
import { deleteAllMessages } from "../../redux/messageSlice";

export default function ChatBoxTitle() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentTask = useSelector((state) => state.current);
  const socket = useContext(SocketContext);

  const me = getUserFromLocalStorage();

  // Everyone on the task except the reader.
  const others = (currentTask.users || []).filter((u) => u._id !== me?._id);

  // Who is online, keyed by id. This used to live in module-level arrays that a
  // setInterval copied into state once a second, for the life of the page, so
  // the header re-rendered sixty times a minute whether anything had changed or
  // not. The interval existed because the socket handlers are registered once
  // and would otherwise close over stale state; updating by function solves
  // that without a timer.
  const [presence, setPresence] = useState({});

  useEffect(() => {
    setPresence({});
    others.forEach((person) => socket.emit("is-online", person._id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTask._id]);

  useEffect(() => {
    const onOnline = (userId) =>
      setPresence((current) => ({ ...current, [userId]: { online: true } }));
    const onOffline = ({ userId, lastseen }) =>
      setPresence((current) => ({
        ...current,
        [userId]: { online: false, lastseen },
      }));

    socket.on("online-received", onOnline);
    socket.on("offline-received", onOffline);
    return () => {
      socket.off("online-received", onOnline);
      socket.off("offline-received", onOffline);
    };
  }, [socket]);

  // A client is talking to the team, not to a named individual: showing every
  // member with their own "last seen" filled the header and put a colleague who
  // was last here six months ago in front of the customer. The team answers as
  // one, so it is presented as one. Somebody on the team, on the other hand, is
  // talking to a particular person and wants their name.
  const viewerIsClient = me?.role === ROLE.CLIENT;

  // Who is who is decided by role, the way the web header does it. Matching the
  // task's client id against the participants looked equivalent and was not:
  // client is not always populated as an object, so the comparison quietly
  // failed and the header fell back to the first person in the array, which on
  // a task carrying a developer is the developer.
  const teamMembers = others.filter((person) => person.role !== ROLE.CLIENT);
  const counterpart =
    others.find((person) => person.role === ROLE.CLIENT) || others[0];

  // For a client, "is anyone there" means the team, not whoever happens to be
  // on the task. For the team, the person they care about is the client.
  const watched =
    me?.role === ROLE.CLIENT ? teamMembers : [counterpart].filter(Boolean);
  const anyoneOnline = watched.some((person) => presence[person._id]?.online);

  const heading = viewerIsClient
    ? t("support", "Support")
    : counterpart?.username || "";

  // Only when somebody is actually there. A stale timestamp on a support team
  // says nothing useful and reads badly.
  const subheading = anyoneOnline
    ? t("online", "Online")
    : !viewerIsClient && counterpart && presence[counterpart._id]?.lastseen
      ? `${t("last_seen")} ${moment(presence[counterpart._id].lastseen).fromNow()}`
      : "";

  // The client's address, for the team only.
  //
  // Taken off the participant rather than the task's client object, because
  // that one is not always populated here, which is the trap the comment above
  // describes. The task route populates users with email, so this is the value
  // that is actually present.
  //
  // A client never sees it: the branch requires the reader not to be one, and
  // the list it reads from has the reader removed anyway.
  const counterpartEmail =
    !viewerIsClient && counterpart?.role === ROLE.CLIENT
      ? counterpart.email || ""
      : "";

  const avatars = viewerIsClient
    ? teamMembers.slice(0, 3)
    : [counterpart].filter(Boolean);

  const goBack = () => {
    dispatch(deleteCurrentTask());
    dispatch(deleteAllMessages());
  };

  return (
    <div className="chatbox-title d-flex align-items-center">
      <div className="d-flex justify-content-between align-items-center w-100">
        <div className="d-flex align-items-center chat-header-text-row">
          <div className="chat-header-avatars">
            {avatars.map((person, index) => (
              <img
                key={person._id}
                src={avatarSrc(person.avatar)}
                onError={onAvatarError}
                alt=""
                data-testid="header-avatar"
                className="team-avatar"
                style={{
                  marginLeft: index === 0 ? 0 : -10,
                  zIndex: avatars.length - index,
                }}
              />
            ))}
            <div
              data-testid="presence"
              className={anyoneOnline ? "online-status" : "offline-status"}
            ></div>
          </div>
          <div className="ms-2 chat-header-text">
            <div style={{ fontSize: "15px", fontWeight: 600, lineHeight: 1.2 }}>
              {heading}
            </div>
            {counterpartEmail && (
              <div className="chat-person-email" title={counterpartEmail}>
                {counterpartEmail}
              </div>
            )}
            {subheading && <div className="taskname">{subheading}</div>}
          </div>
        </div>
        <div>
          <button className="go-back" onClick={goBack}>
            <i className="bi bi-chevron-right"></i>
          </button>
        </div>
        {/* <div>
          <button className="chatbox-title-right-button">
            <i className="bi bi-three-dots-vertical"></i>
          </button>
        </div> */}
      </div>
    </div>
  );
}
